import "./style.css";
import "./library.css";
import "./desktop-reference.css";
import { LibraryUI } from "./library-ui.js";
import { ArchiveScene } from "./scene.js";
import { BootSequence } from "./boot.js";
import { createRollingNumber } from "./rolling-number.js";
import { MobilePlayer } from "./mobile-player.js";
import { animateLyricScroll } from "./lyric-scroll.js";
import { playbackModeLabel, playbackModeMarkup, playIconMarkup } from "./playback-icons.js";
import {
  Player,
  fmt,
  json,
  normalizeLocal,
  normalizeCloud,
  mediaUrl,
  localPath,
} from "./player.js";

const $ = (id) => document.getElementById(id);
const updateTrackNumber = createRollingNumber($("selectedNumber"));
let bootSequence = null;
let reelSignature = "";
const escape = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
function read(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem("rhine:" + key)) ?? fallback;
  } catch {
    return fallback;
  }
}
function save(key, value) {
  try {
    localStorage.setItem("rhine:" + key, JSON.stringify(value));
  } catch {}
}
const state = {
  local: [],
  cloud: [],
  list: [],
  selected: 0,
  source: "local",
  collection: "我的曲库",
  mode: "archive",
  dialog: null,
  tabMode: read("tabMode", matchMedia("(min-width: 901px) and (pointer: fine)").matches),
  theme: read("theme", "light"),
  fontScale: read("fontScale", null),
  reduced: read(
    "reduced",
    matchMedia("(prefers-reduced-motion: reduce)").matches,
  ),
  favorites: read("favorites", []),
  savedSongs: read("savedSongs", []),
  searchSource: "cloud",
  query: "",
  results: [],
  searching: false,
  searchError: "",
  searchToken: 0,
  detailTab: "info",
  likedOnly: false,
  showTranslation: read("showTranslation", true),
  splitLyrics: read("splitLyrics", true),
  autoFollowPlayback: read("autoFollowPlayback", true),
};
const player = new Player();
player.autoTranslationEnabled = state.showTranslation;
player.splitLongLyrics = state.splitLyrics;
player.preferOriginalFlac = read("preferOriginalFlac", false);
player.audio.volume = Math.max(
  0,
  Math.min(1, Number(read("volume", 0.7)) || 0),
);
player.mode = read("mode", "all");
if (!["all", "one", "shuffle"].includes(player.mode)) player.mode = "all";
const scene = new ArchiveScene(
  $("scene"),
  (index) => select(index),
  () => openDetail(),
);
scene.setReduced(state.reduced);
const mobilePlayer = new MobilePlayer({
  scene,
  player,
  translationEnabled: () => state.showTranslation,
  toggleTranslation: () => actions["lyrics-translation"](),
  toggleLyricSplit: () => actions["lyrics-split"](),
  isFavorite: (key) => state.favorites.includes(key),
  toggleFavorite,
  cycleMode: () => actions.mode(),
  onVisibility: (visible) => {
    if (!visible) {
      const song = currentSelection();
      if (song) scene.select(song, state.selected, state.list.length);
      scene.setDetail(state.mode === "detail");
      scene.invalidate();
    }
  },
  openLibrary: showLibrary,
});
function playSong(song, list, queueIndex) {
  const result = player.play(song, list, queueIndex);
  if (song) mobilePlayer.open();
  return result;
}
const library = new LibraryUI({
  showDialog,
  panelHeader,
  useCollection,
  select,
  openDetail,
  toast,
  allTracks: () => state.local,
  currentDialog: () => state.dialog,
  play: playSong,
  reloadTracks: () => loadLocal(true),
});
let toastTimer,
  lyricsSignature = "",
  lastLyric = -2,
  queueSignature = "";
let manualLyricsUntil = 0;
let cancelLyricScroll = () => {};
function toast(message) {
  if (!message) return;
  $("toast").textContent = message;
  $("toast").classList.add("visible");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => $("toast").classList.remove("visible"), 4200);
}
function currentSelection() {
  return state.list[state.selected];
}
function setTheme(theme) {
  state.theme = theme;
  save("theme", theme);
  document.documentElement.dataset.theme = theme;
  document.querySelector("meta[name=theme-color]").content =
    theme === "dark" ? "#0a0f14" : "#e7e7e1";
  scene.setTheme(theme === "dark");
  paintInk();
}
function applyFontScale() {
  const mobile = innerWidth <= 900 || innerWidth / innerHeight < 1.2 ||
    (matchMedia("(pointer: coarse)").matches && innerHeight <= 700 && innerWidth / innerHeight >= 1.4);
  document.documentElement.style.setProperty(
    "--font-scale", String(state.fontScale ?? (mobile ? 1 : 0.9)),
  );
}
function size() {
  applyFontScale();
  const height = window.visualViewport?.height || innerHeight;
  document.documentElement.style.setProperty(
    "--viewport-top",
    (window.visualViewport?.offsetTop || 0) + "px",
  );
  let probe = $("viewportInsets");
  if (!probe) {
    probe = document.createElement("div");
    probe.id = "viewportInsets";
    probe.setAttribute("aria-hidden", "true");
    document.body.append(probe);
  }
  const insets = getComputedStyle(probe);
  const top = parseFloat(insets.paddingTop) || 0,
    bottom = parseFloat(insets.paddingBottom) || 0;
  const left = parseFloat(insets.paddingLeft) || 0,
    right = parseFloat(insets.paddingRight) || 0;
  const compactLandscape = matchMedia("(pointer: coarse)").matches && height <= 700 && innerWidth / height >= 1.4;
  const mobile = innerWidth <= 900 || innerWidth / height < 1.2 || compactLandscape;
  const s = mobile
    ? Math.max(0.75, Math.min(1, innerWidth / 500))
    : height / 1080;
  document.documentElement.style.setProperty("--app-height", height + "px");
  document.documentElement.style.setProperty(
    "--stage-height",
    Math.max(0, height - top - bottom) / s + "px",
  );
  document.documentElement.style.setProperty("--scale", s);
  document.documentElement.style.setProperty(
    "--stage-width",
    Math.max(0, innerWidth - left - right) / s + "px",
  );
  document.documentElement.style.setProperty("--hud-left", left + "px");
  document.documentElement.style.setProperty("--hud-top", top + "px");
  paintInk();
}
function paintInk() {
  const canvas = $("ink");
  if (!canvas) return;
  const s = innerHeight / 1080;
  const w = innerWidth / s;
  canvas.width = Math.round(w);
  canvas.height = 1080;
  const c = canvas.getContext("2d");
  c.clearRect(0, 0, w, 1080);
  // NativeHud::archiveMask, adapted to a browser Canvas command layer.
  if (
    !state.dialog &&
    state.mode === "archive" &&
    innerWidth / innerHeight > 1.2
  ) {
    const g = c.createLinearGradient(w * 0.44, 0, w, 0);
    const rgb = state.theme === "dark" ? "16,27,32" : "231,231,225";
    g.addColorStop(0, `rgba(${rgb},0)`);
    g.addColorStop(1, `rgba(${rgb},.52)`);
    c.fillStyle = g;
    c.fillRect(0, 0, w, 984);
  }
}
function select(index, { scroll = false } = {}) {
  if (!state.list.length) {
    state.selected = 0;
    renderArchive();
    return;
  }
  state.selected =
    ((index % state.list.length) + state.list.length) % state.list.length;
  const song = currentSelection();
  scene.select(song, state.selected, state.list.length);
  scene.prefetchCovers(state.list, state.selected);
  renderArchive();
  if (state.mode === "detail") renderDetail();
  if (scroll) centerTrackReel();
}
function centerTrackReel() {
  if (!state.tabMode || $("trackReel").hidden) return;
  const current = $("reelItems").querySelector("[aria-current=true]");
  if (!current) return;
  const reel = $("trackReel"), bounds = reel.getBoundingClientRect();
  const item = current.getBoundingClientRect();
  const scale = bounds.height / reel.clientHeight;
  reel.scrollTo({top: reel.scrollTop + (item.top + item.height / 2 - bounds.top - bounds.height / 2) / scale,
    behavior: state.reduced ? "instant" : "smooth"});

}
function followPlayingSong() {
  const song = player.current;
  if (!song) return;
  let index = state.list.findIndex((item) => item.key === song.key);
  if (index < 0) {
    state.list = [...player.queue];
    state.source = song.source;
    state.collection = "播放队列";
    index = player.index;
  }
  select(index, { scroll: state.tabMode });
}
function useCollection(list, source, title) {
  state.list = list;
  state.source = source;
  state.collection = title;
  state.likedOnly = false;
  state.selected = 0;
  state.mode = "archive";
  scene.setDetail(false);
  closeDialog();
  select(0);
}
function renderArchive() {
  const song = currentSelection();
  $("libraryName").textContent = state.collection;
  $("collectionLabel").textContent = "MUSIC DATABASE / " + state.collection;
  $("selectedTitle").textContent =
    song?.title || (state.searching ? "正在检索音乐…" : "资料库暂无曲目");
  $("selectedMeta").textContent = song
    ? `${song.artist} / ${song.album || state.collection}`
    : "选择音乐检索，或刷新本地曲库";
  const session =
    state.source === "local" ? "LOCAL LIBRARY" : "NETEASE / GUEST SESSION";
  const detail = state.mode === "detail" && song;
  const identifier = detail ? String(song.id ?? "").replace(/\D/g, "") : "";
  $("trackNumberLabel").textContent = detail ? "MUSIC DATABASE" : "TRACK / SELECT";
  updateTrackNumber(
    detail
      ? identifier || String(state.selected + 1).padStart(2, "0")
      : String(state.list.length ? state.selected + 1 : 0).padStart(2, "0"),
  );
  $("trackTotal").textContent = String(state.list.length).padStart(2, "0");
  $("trackCaption").hidden = !detail;
  $("trackCaption").innerHTML = detail
    ? `<span>DRAG TO INSPECT</span><button data-action="artwork">查看封面 ↗</button><span>${session}</span>`
    : "";
  $("columnLabel").textContent = state.collection;
  $("columnCount").textContent =
    state.source === "local" ? "COLLECTION 01 / 02" : "COLLECTION 02 / 02";
  $("sessionLabel").textContent = session;
  $("hud").classList.toggle("detail", state.mode === "detail");
  $("hud").classList.toggle("dialog-open", Boolean(state.dialog));
  $("detailPanel").hidden = state.mode !== "detail";
  $("archiveInfo").hidden = state.tabMode && Boolean(song);
  $("trackReel").hidden = !state.tabMode || !song || state.mode === "detail";
  $("tabMode").setAttribute("aria-pressed", state.tabMode);
  $("ticks").innerHTML = Array.from(
    { length: Math.min(state.list.length, 8) },
    (_, i) =>
      `<button data-action="slot" data-index="${Math.floor(state.selected / 8) * 8 + i}" class="${i === state.selected % 8 ? "active" : ""}" aria-label="选择第 ${Math.floor(state.selected / 8) * 8 + i + 1} 首"></button>`,
  ).join("");
  if (state.tabMode) {
    const signature = JSON.stringify(state.list.map(song => [song.key, song.title]));
    if (signature !== reelSignature) {
      $("reelItems").innerHTML = state.list.map((s, i) =>
        `<button data-action="select" data-index="${i}" aria-current="false">${escape(s.title)}</button>`
      ).join("");
      reelSignature = signature;
    }
    for (const button of $("reelItems").children)
      button.setAttribute("aria-current", String(Number(button.dataset.index) === state.selected));
  }

  document
    .querySelectorAll("nav button")
    .forEach((b) =>
      b.setAttribute(
        "aria-current",
        b.dataset.action === state.dialog ? "page" : "false",
      ),
    );
  paintInk();
}
async function loadLocal(refresh = false) {
  if (!state.local.length || refresh) {
    const [legacy, catalog] = await Promise.allSettled([
      json("/music/playlist"),
      library.refresh(),
    ]);
    if (legacy.status === "rejected" && catalog.status === "rejected")
      throw legacy.reason;
    state.local = [
      ...(catalog.status === "fulfilled" ? catalog.value.tracks : []),
      ...(legacy.status === "fulfilled" && Array.isArray(legacy.value.playlist)
        ? legacy.value.playlist.map(normalizeLocal)
        : []),
    ];
  }
  return state.local;
}
async function showLibrary() {
  await loadLocal();
  return library.show();
}
async function showAllLibrary() {
  try {
    const list = await loadLocal();
    useCollection(list, "local", "我的曲库");
  } catch (error) {
    toast("本地曲库加载失败：" + error.message);
    $("selectedTitle").textContent = "本地曲库暂不可用";
    $("selectedMeta").textContent = "请在设置中刷新曲库，或使用音乐检索";
  }
}
function openDetail(song = currentSelection()) {
  if (!song) {
    toast("请先选择一首歌曲");
    return;
  }
  if (song !== currentSelection()) {
    const list = song.source === "local" ? state.local : player.queue;
    useCollection(
      list.length ? list : [song],
      song.source,
      song.source === "local" ? song.album || "我的曲库" : "播放队列",
    );
    state.selected = Math.max(
      0,
      state.list.findIndex((s) => s.key === song.key),
    );
  }
  closeDialog();
  state.mode = "detail";
  state.detailTab = "info";
  scene.select(song, state.selected, state.list.length);
  scene.setDetail(true);
  renderArchive();
  renderDetail();
  if (song.source === "cloud" && !song.cover) fetchCloudDetail(song);
}
async function fetchCloudDetail(song) {
  try {
    const data = await json(
      "/netease/song/detail?ids=" + encodeURIComponent(song.id),
    );
    const detail = data.songs?.[0];
    if (detail) {
      Object.assign(song, normalizeCloud(detail));
      if (song.key === currentSelection()?.key) {
        scene.select(song, state.selected, state.list.length);
        renderDetail();
      }
    }
  } catch {}
}
function renderDetail() {
  const song = currentSelection();
  const panel = $("detailPanel");
  panel.hidden = state.mode !== "detail";
  if (!song) return;
  const liked = state.favorites.includes(song.key);
  const albumSongs = state.list
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.album && item.album === song.album);
  const albumSeconds = albumSongs.reduce(
    (sum, { item }) => sum + (item.duration || 0),
    0,
  );
  panel.innerHTML = `<p class="eyebrow record-heading"><span class="record-number">01</span><span>TRACK RECORD / 歌曲档案</span><span class="record-id">ID · ${escape(song.id)}</span></p><p class="record-album">${escape(song.album || "AUDIO ARCHIVE / 音频档案")}</p><h3>${escape(song.title)}</h3><p class="detail-artist">${escape(song.artist)}</p><div class="actions-grid"><button data-action="favorite" aria-pressed="${liked}">${liked ? "♥ 已喜欢" : "♡ 喜欢"}</button><button data-action="lyrics">同步歌词 ↗</button><button data-action="artwork">查看封面 ↗</button><button data-action="play-selected" class="primary">${playIconMarkup()}<span>面板内播放</span></button><button data-action="enqueue">＋ 加入队列</button><button data-action="play-next">▷ 下一首播放</button></div><div class="detail-facts"><div><small>ARTIST / 歌手</small><span>${escape(song.artist)}</span></div><div><small>ALBUM / 专辑</small><span>${escape(song.album)}</span></div><div><small>DURATION / 时长</small><span>${song.duration ? fmt(song.duration) : "播放后读取"}</span></div><div><small>PLAYBACK / 播放方式</small><span>${song.browserAudioFormat ? "服务器兼容音源 / " + escape(song.browserAudioFormat) + "（保留 FLAC 原件）" : song.source === "local" ? "本地文件 / 浏览器播放" : "网易云 / 在线播放"}${song.preview ? " · 试听" : ""}</span></div></div><div class="panel-tabs"><button data-action="detail-info" aria-pressed="${state.detailTab === "info"}">歌曲百科</button><button data-action="detail-album" aria-pressed="${state.detailTab === "album"}">专辑信息</button><button data-action="detail-same" aria-pressed="${state.detailTab === "same"}">同专辑</button></div><div class="panel-description" id="detailBody"></div>`;
  if (state.detailTab === "album")
    $("detailBody").innerHTML = `<div class="detail-facts panel-facts"><div><small>ALBUM / 专辑</small><span>${escape(song.album || "未命名专辑")}</span></div><div><small>ARTIST / 歌手</small><span>${escape(song.artist)}</span></div><div><small>TRACKS / 曲目</small><span>${albumSongs.length || 1} 首</span></div><div><small>DURATION / 总时长</small><span>${albumSeconds ? fmt(albumSeconds) : "播放后读取"}</span></div></div>`;
  else if (state.detailTab === "same")
    $("detailBody").innerHTML = albumSongs.length
      ? '<ol class="song-list">' +
        albumSongs
          .map(
            ({ item, index }) =>
              `<li class="song-row ${item.key === player.current?.key ? "current" : ""}"><span class="index">${String(index + 1).padStart(2, "0")}</span><button class="song-main" data-action="detail-play" data-index="${index}" aria-label="播放 ${escape(item.title)}"><strong>${escape(item.title)}</strong><small>${escape(item.artist)}</small></button></li>`,
          )
          .join("") +
        "</ol>"
      : '<p class="list-message">暂无同专辑曲目。</p>';
  else
    $("detailBody").textContent =
      song.source === "local"
        ? "来源：个人本地音乐资料库\n音频、封面与同名 LRC 歌词由音乐服务器提供。"
        : "来源：网易云音乐\n仅播放接口返回的可用音源；部分歌曲可能仅提供试听。";
}
function panelHeader(label, sub = "") {
  return `<div class="panel-title"><h2>${label}</h2><button data-action="close-dialog" aria-label="关闭面板">×</button></div>${sub ? `<p class="eyebrow">${sub}</p>` : ""}`;
}
function showDialog(name) {
  state.dialog = name;
  $("hud").dataset.dialog = name;
  if (name === "lyrics" && player.current) {
    scene.select(player.current, player.index, player.queue.length);
    scene.setDetail(true);
  }
  $("dialogPanel").hidden = false;
  renderArchive();
}
function closeDialog() {
  state.dialog = null;
  delete $("hud").dataset.dialog;
  const selected = currentSelection();
  if (selected) scene.select(selected, state.selected, state.list.length);
  scene.setDetail(state.mode === "detail");
  $("dialogPanel").hidden = true;
  renderArchive();
}
function songList(list, action = "result-play", remove = false) {
  if (!list.length) return '<p class="list-message">暂无曲目。</p>';
  return (
    '<ol class="song-list">' +
    list
      .map(
        (s, i) =>
          `<li class="song-row ${s.key === player.current?.key ? "current" : ""}"><span class="index">${String(i + 1).padStart(2, "0")}</span><button class="song-main" data-action="${action}" data-index="${i}" aria-label="播放 ${escape(s.title)}"><strong>${escape(s.title)}</strong><small>${escape(s.artist)} / ${escape(s.album)}</small></button><button class="row-action" data-action="${remove ? "queue-remove" : "result-detail"}" data-index="${i}" aria-label="${remove ? "移出队列" : "查看详情"}" ${remove && i === player.index ? "disabled" : ""}>${remove ? "×" : "↗"}</button></li>`,
      )
      .join("") +
    "</ol>"
  );
}
function showSearch() {
  showDialog("search");
  renderSearch();
  requestAnimationFrame(() => $("searchInput")?.focus());
}
function renderSearch() {
  const panel = $("dialogPanel");
  panel.innerHTML =
    panelHeader("音乐检索", "SEARCH / MUSIC DATABASE") +
    `<form id="searchForm" class="search-form"><input id="searchInput" autocomplete="off" maxlength="120" placeholder="歌曲 / 歌手 / 专辑" aria-label="搜索关键词" value="${escape(state.query)}"><button type="submit">检索 →</button></form><div class="source-tabs"><button data-action="search-cloud" aria-pressed="${state.searchSource === "cloud"}">网易云音乐</button><button data-action="search-local" aria-pressed="${state.searchSource === "local"}">本地曲库</button></div><p class="search-status" id="searchStatus">${state.searching ? "正在检索…" : state.searchError ? escape(state.searchError) : state.results.length ? `找到 ${state.results.length} 首曲目` : "输入关键词开始检索"}</p><div id="searchResults">${state.results.length ? songList(state.results) : ""}</div>`;
}
async function search() {
  const query = $("searchInput")?.value.trim() ?? state.query;
  if (!query) {
    toast("请输入搜索关键词");
    return;
  }
  state.query = query;
  state.searching = true;
  state.searchError = "";
  const token = ++state.searchToken;
  const source = state.searchSource;
  renderSearch();
  try {
    let list;
    if (source === "local") {
      list = (await loadLocal()).filter((s) =>
        (s.title + " " + s.artist + " " + s.album)
          .toLowerCase()
          .includes(query.toLowerCase()),
      );
    } else {
      let data;
      try {
        data = await json(
          "/netease/cloudsearch?keywords=" +
            encodeURIComponent(query) +
            "&type=1&limit=50",
        );
      } catch {
        data = await json(
          "/netease/search?keywords=" + encodeURIComponent(query) + "&limit=50",
        );
      }
      list = (data.result?.songs || []).map(normalizeCloud);
    }
    if (token !== state.searchToken) return;
    state.results = list;
    state.searching = false;
    if (source === "cloud") state.cloud = list;
    if (!list.length) state.searchError = "没有找到匹配的曲目。";
  } catch (error) {
    if (token !== state.searchToken) return;
    state.results = [];
    state.searching = false;
    state.searchError = "检索失败：" + error.message;
  }
  if (state.dialog === "search") renderSearch();
}
function showSettings() {
  showDialog("settings");
  const row = (label, controls) =>
    `<div class="setting-row"><span>${label}</span><div class="choices">${controls}</div></div>`;
  const toggle = (action, on, onLabel = "开启") =>
    `<button data-action="${action}-on" aria-pressed="${on}">${onLabel}</button><button data-action="${action}-off" aria-pressed="${!on}">关闭</button>`;
  const fontScale = state.fontScale ?? (innerWidth <= 900 ? 1 : 0.9);
  $("dialogPanel").innerHTML =
    `<div class="panel-title settings-title"><div><h2>SYSTEM SETTINGS</h2><p class="eyebrow">终端偏好设置</p></div><button data-action="close-dialog" class="panel-back">← 返回</button></div>` +
    `<p class="eyebrow settings-breadcrumb">RHINE LAB / MUSIC DATABASE / SYSTEM PREFERENCES</p>` +
    `<p class="settings-section">PROVIDER ACCESS / 音乐平台</p>` +
    row("LOCAL LIBRARY / 本地音乐", `<button data-action="refresh-local" class="value-button">刷新曲库 ↻</button>`) +
    row("FAVORITES / 我的喜欢", `<button data-action="favorites" class="value-button">${state.favorites.length} 首 ↗</button>`) +
    `<p class="settings-section">TERMINAL PREFERENCES / 终端偏好</p>` +
    row("COLOR THEME / 界面配色", `<button data-action="theme-light" aria-pressed="${state.theme === "light"}">浅色</button><button data-action="theme-dark" aria-pressed="${state.theme === "dark"}">暗色</button>`) +
    row("RENDER QUALITY / 画面质量", [["auto","自动"],["balanced","平衡"],["high","精细"],["low","省电"]].map(([key,label])=>`<button data-action="quality-${key}" aria-pressed="${scene.qualityPreference===key}">${label}</button>`).join("")) +
    row("AUTO FOLLOW / 随播放滚动封面", toggle("follow", state.autoFollowPlayback)) +
    row("REDUCED MOTION / 动态效果", toggle("motion", !state.reduced, "完整动效")) +
    row("STARTUP ANIMATION / 开场动画", `<button data-action="replay-boot">重播 ↗</button>`) +
    row("LIBRARY VIEW / 资料库视图", `<button data-action="view-card" aria-pressed="${!state.tabMode}">曲目信息</button><button data-action="view-list" aria-pressed="${state.tabMode}">曲目纵列</button>`) +
    row("FONT SCALE / 全局字号", [[0.85,"较小"],[0.9,"小"],[1,"标准"],[1.1,"大"],[1.2,"较大"]].map(([value,label])=>`<button data-action="font-${Math.round(value*100)}" aria-pressed="${fontScale === value}" aria-label="字号 ${Math.round(value*100)}%">${label}</button>`).join("")) +
    row("LYRIC SPLIT / 长句自动拆分", `<button data-action="lyrics-split" aria-pressed="${state.splitLyrics}">${state.splitLyrics ? "开启" : "关闭"}</button>`) +
    row("LOSSLESS SOURCE / FLAC 原音质优先", `<button data-action="flac-on" aria-pressed="${player.preferOriginalFlac}">开启</button><button data-action="flac-off" aria-pressed="${!player.preferOriginalFlac}">关闭</button>`) +
    `<p class="setting-description">FLAC 原音质仅用于本站上传的文件。浏览器无法解码时自动切换 MP3 320 kbps；已在播放的歌曲下次开始时生效。只拆分无译文的长句，译文仍与原句对齐。</p>` +
    `<p class="setting-description">主题、音量和喜欢的曲目保存在当前浏览器。播放音乐前，请先点击播放；无需安装桌面软件。iPhone 可在 Safari 中点分享 → 添加到主屏幕，从主屏幕图标独立全屏打开。</p>` +
    `<div class="keyboard-grid"><span><kbd>SPACE</kbd>　播放 / 暂停</span><span><kbd>ESC</kbd>　关闭 / 返回</span><span><kbd>↑ ↓</kbd>　选择曲目</span><span><kbd>ENTER</kbd>　歌曲详情</span><span><kbd>← →</kbd>　切换音源</span><span><kbd>/</kbd>　音乐检索</span><span><kbd>T</kbd>　切换资料库视图</span><span><kbd>L</kbd>　同步歌词</span></div>` +
    `<p class="setting-description">界面与模型基于 Rhine Music Database 移植。<a href="/assets/Rhine-LICENSE.txt" target="_blank" rel="noopener">Rhine 许可 ↗</a> · <a href="/assets/Three-LICENSE.txt" target="_blank" rel="noopener">Three.js 许可 ↗</a> · <a href="/assets/Lazer-LICENSE.txt" target="_blank" rel="noopener">Lazer 许可 ↗</a> · <a href="/assets/MiSans-license.pdf" target="_blank" rel="noopener">字体许可 ↗</a></p>`;
}
function showQueue() {
  showDialog("queue");
  queueSignature = "";
  renderQueue();
}
function renderQueue() {
  const signature =
    player.queue.map((s) => s.key).join("|") + ":" + player.index;
  if (signature === queueSignature) return;
  queueSignature = signature;
  $("dialogPanel").innerHTML =
    panelHeader("播放队列", `QUEUE / ${player.queue.length} 首`) +
    `<div class="panel-close-row"><span>正在播放 ${player.index >= 0 ? player.index + 1 : "—"} / ${player.queue.length}</span><button data-action="clear-queue">清空待播</button></div>` +
    songList(player.queue, "queue-play", true);
}
function showLyrics() {
  if (mobilePlayer.media.matches) {
    mobilePlayer.open("lyrics");
    return;
  }
  showDialog("lyrics");
  cancelLyricScroll();
  lyricsSignature = "";
  lastLyric = -2;
  renderLyrics();
}
function renderLyrics() {
  const song = player.current;
  const signature =
    (song?.key || "") +
    ":" +
    player.lyrics.length +
    ":" +
    player.lyricMode +
    ":" +
    player.wordLyrics.length +
    ":" + state.splitLyrics +
    ":" +
    state.showTranslation +
    ":" +
    player.translationState +
    ":" +
    player.translations.length;
  if (signature !== lyricsSignature) {
    cancelLyricScroll();
    lyricsSignature = signature;
    $("dialogPanel").innerHTML =
      `<div class="lyric-heading"><div><h2>${escape(song?.title || "等待播放")}</h2><p>${escape(song?.artist || "")}</p></div><button data-action="close-dialog" aria-label="关闭歌词">关闭</button></div>` +
      `<div class="lyric-mode"><button data-action="lyrics-line" aria-pressed="${player.lyricMode === "line"}">逐行歌词</button><button data-action="lyrics-word" aria-pressed="${player.lyricMode === "word"}" ${!player.wordLyrics.length ? "disabled" : ""}>逐字歌词</button><button data-action="lyrics-translation" aria-pressed="${state.showTranslation}">译文${state.showTranslation ? " 开" : " 关"}</button></div><p class="translation-status">${state.showTranslation ? (player.translationState === "pending" ? "正在获取译文…" : player.translationState === "error" ? escape(player.translationError) + ' <button data-action="translation-retry">重试</button>' : player.translationState === "original" ? "原文为中文，无需重复翻译" : escape(player.translationProvider)) : ""}</p><div class="lyrics-content" id="lyricsContent">${player.lyrics.length ? player.lyrics.map((line, i) => `<button data-action="lyric-seek" data-index="${i}"><span>${player.lyricMode === "word" && line.words ? line.words.map((w, n) => `<span class="lyric-word" data-word="${n}">${escape(w.text)}</span>`).join("") : escape(line.text || "♪")}</span>${state.showTranslation && line.translation ? `<span class="lyric-translation">${escape(line.translation)}</span>` : ""}</button>`).join("") : `<p class="list-message">${!song ? "选择一首歌曲开始播放。" : player.loading ? "正在读取歌词…" : "这首曲目暂无同步歌词。"}</p>`}</div><div class="lyric-footer"><div><button data-action="lyrics-earlier" aria-label="歌词提前 0.2 秒">−</button><span id="lyricOffsetLabel">偏移 ${player.lyricOffset.toFixed(1)} 秒</span><button data-action="lyrics-later" aria-label="歌词延后 0.2 秒">＋</button><button data-action="lyrics-reset">重置</button></div><button data-action="lyrics-follow" id="lyricsFollow" ${manualLyricsUntil ? "" : "hidden"}>回到当前句</button></div>`;
    $("dialogPanel").querySelector('[data-action="lyrics-translation"]')?.insertAdjacentHTML("afterend", `<button data-action="lyrics-split" aria-pressed="${state.splitLyrics}">拆句${state.splitLyrics ? " 开" : " 关"}</button>`);
    const panel = $("dialogPanel");
    const preferences = document.createElement("details");
    preferences.className = "lyric-preferences";
    const summary = document.createElement("summary");
    summary.textContent = "歌词设置 / 偏移";
    preferences.append(summary);
    const controls = document.createElement("div");
    controls.className = "lyric-preferences-body";
    controls.append(panel.querySelector(".lyric-mode"), panel.querySelector(".translation-status"), panel.querySelector(".lyric-footer > div"));
    preferences.append(controls);
    panel.querySelector(".lyric-footer").prepend(preferences);
    lastLyric = -2;
  }
  let active = -1;
  for (let i = 0; i < player.lyrics.length; i++) {
    if (player.lyrics[i].time + player.lyricOffset <= player.audio.currentTime) active = i;
    else break;
  }
  if (manualLyricsUntil && Date.now() > manualLyricsUntil) {
    manualLyricsUntil = 0;
    lastLyric = -2;
  }
  if (active !== lastLyric) {
    const previous = lastLyric;
    lastLyric = active;
    const items = $("lyricsContent")?.querySelectorAll("button") || [];
    if (previous < 0) $("lyricsContent")?.querySelector("button.active")?.classList.remove("active");
    else items[previous]?.classList.remove("active");
    items[active]?.classList.add("active");
    const line = items[active];
    if (line && !manualLyricsUntil) {
      const box = $("lyricsContent");
      cancelLyricScroll();
      cancelLyricScroll = animateLyricScroll(box,
        line.offsetTop - box.offsetTop - (matchMedia("(min-width: 901px) and (min-aspect-ratio: 6/5) and (pointer: fine)").matches ? 155 : box.clientHeight * 0.42 - line.clientHeight / 2),
        { reduced: state.reduced, duration: 420, interrupted: () => Boolean(manualLyricsUntil) || state.dialog !== "lyrics" });
    }
  }
  if ($("lyricOffsetLabel")) $("lyricOffsetLabel").textContent = `偏移 ${player.lyricOffset.toFixed(1)} 秒`;
  if ($("lyricsFollow")) $("lyricsFollow").hidden = !manualLyricsUntil;
  if (active >= 0 && player.lyricMode === "word") {
    const row = player.lyrics[active];
    $("lyricsContent")
      ?.querySelectorAll("button")
      [active]?.querySelectorAll(".lyric-word")
      .forEach((el, index) => {
        const word = row.words[index];
        const end =
          word.end ??
          row.words[index + 1]?.time ??
          player.lyrics[active + 1]?.time ??
          word.time + 0.5;
        const progress = Math.max(
          0,
          Math.min(
            1,
            (player.audio.currentTime - word.time) /
              Math.max(0.05, end - word.time),
          ),
        );
        el.style.setProperty("--word-progress", progress * 100 + "%");
      });
  }
}
async function showDiscover() {
  showDialog("discover");
  $("dialogPanel").innerHTML =
    panelHeader("发现音乐", "DISCOVER / LOCAL & CLOUD") +
    '<p class="list-message">正在读取音乐资料…</p>';
  try {
    const data = await json("/netease/top/playlist?limit=12&order=hot");
    if (state.dialog !== "discover") return;
    state.discover = data.playlists || [];
    renderDiscover();
  } catch {
    if (state.dialog === "discover") {
      state.discover = [];
      renderDiscover();
    }
  }
}
function renderDiscover() {
  $("dialogPanel").innerHTML =
    panelHeader("发现音乐", "DISCOVER / LOCAL & CLOUD") +
    `<div class="actions-grid"><button data-action="library" class="primary">本地音乐 →</button><button data-action="search">网易云检索 →</button><button data-action="favorites">我的喜欢 →</button></div><p class="setting-description">网易云热门歌单</p>` +
    (state.discover.length
      ? '<ol class="song-list">' +
        state.discover
          .map(
            (p, i) =>
              `<li class="song-row"><span class="index">${String(i + 1).padStart(2, "0")}</span><button class="song-main" data-action="playlist" data-index="${i}"><strong>${escape(p.name)}</strong><small>${escape(p.creator?.nickname || "网易云音乐")} / ${p.trackCount || 0} 首</small></button><span>↗</span></li>`,
          )
          .join("") +
        "</ol>"
      : '<p class="list-message">热门歌单暂不可用。你仍可搜索歌曲或播放本地音乐。</p>');
}
async function openPlaylist(index) {
  const entry = state.discover?.[index];
  if (!entry) return;
  $("dialogPanel").innerHTML =
    panelHeader(escape(entry.name)) +
    '<p class="list-message">正在载入歌单…</p>';
  try {
    const data = await json("/netease/playlist/detail?id=" + entry.id);
    if (state.dialog !== "discover") return;
    const list = (data.playlist?.tracks || []).map(normalizeCloud);
    if (!list.length) throw new Error("歌单暂无可用曲目");
    state.cloud = list;
    useCollection(list, "cloud", entry.name);
  } catch (error) {
    toast("歌单加载失败：" + error.message);
    if (state.dialog === "discover") renderDiscover();
  }
}
function renderPlayer() {
  const song = player.current,
    audio = player.audio;
  if (
    song &&
    song.source === "cloud" &&
    Number.isFinite(audio.duration) &&
    song.duration > audio.duration + 2
  )
    song.preview = true;
  $("nowTitle").textContent = song?.title || "选择歌曲开始播放";
  $("nowArtist").textContent = player.loading
    ? "正在载入音源…"
    : song
      ? song.artist + (song.preview ? " · 试听 " + fmt(audio.duration) : "") + (player.playbackFormat ? " · " + player.playbackFormat : "")
      : "本地曲库 / 网易云音乐";
  const notice = $("sourceNotice");
  notice.hidden = !player.failed || !player.sourceState;
  if (!notice.hidden) {
    $("sourceNoticeText").textContent = player.sourceState.message;
    const alternative = player.sourceState.alternative;
    $("sourceAlternative").hidden = !alternative;
    $("sourceAlternative").textContent = alternative ? `播放推荐版本：${alternative.title} ↗` : "";
  }
  if ($("nowCover").dataset.key !== song?.key) {
    $("nowCover").dataset.key = song?.key || "";
    const cover = $("nowCover");
    cover.replaceChildren();
    if (song?.cover) {
      const img = document.createElement("img");
      img.alt = "";
      img.src = song.cover;
      img.onerror = () => {
        img.remove();
        cover.textContent = "R";
      };
      cover.append(img);
    } else cover.textContent = "R";
  }
  const playButton = $("playButton");
  const playbackState = player.loading ? "loading" : audio.paused ? "play" : "pause";
  if (playButton.dataset.state !== playbackState) {
    playButton.dataset.state = playbackState;
    playButton.innerHTML = playbackState === "loading"
      ? '<span class="transport-loading" aria-hidden="true">···</span>'
      : playbackState === "play"
        ? '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 4.8c0-.76.83-1.24 1.49-.86l11.08 6.96a1.3 1.3 0 0 1 0 2.2L8.49 20.06A.99.99 0 0 1 7 19.2V4.8Z" fill="currentColor"/></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="5.5" y="4" width="4.5" height="16" rx="1" fill="currentColor"/><rect x="14" y="4" width="4.5" height="16" rx="1" fill="currentColor"/></svg>';
  }
  playButton.setAttribute("aria-label", player.loading ? "正在载入" : audio.paused ? "播放" : "暂停");
  playButton.setAttribute("aria-busy", String(player.loading));
  $("timeLabel").textContent =
    fmt(audio.currentTime) + " / " + fmt(audio.duration);
  if (Number.isFinite(audio.duration) && song && song.source === "local")
    song.duration = audio.duration;
  if (document.activeElement !== $("seek"))
    $("seek").value =
      Number.isFinite(audio.duration) && audio.duration > 0
        ? (audio.currentTime / audio.duration) * 1000
        : 0;
  $("seek").style.setProperty("--progress", Number($("seek").value) / 10 + "%");
  $("seek").disabled = !Number.isFinite(audio.duration);
  $("volume").value = Math.round(audio.volume * 100);
  $("volumeControl").style.setProperty("--volume-percent", Math.round((audio.muted ? 0 : audio.volume) * 100) + "%");
  $("muteButton").textContent = audio.muted ? "静音" : "音量";
  $("muteButton").setAttribute("aria-label", audio.muted ? "取消静音" : "静音");
  if ($("modeButton").dataset.mode !== player.mode) {
    $("modeButton").dataset.mode = player.mode;
    $("modeButton").innerHTML = playbackModeMarkup(player.mode);
  }
  $("modeButton").setAttribute("aria-label", playbackModeLabel(player.mode) + "，点击切换");
  if (state.dialog === "queue") renderQueue();
  if (state.dialog === "lyrics") renderLyrics();
  if ("mediaSession" in navigator)
    navigator.mediaSession.playbackState = audio.paused ? "paused" : "playing";
  if (
    navigator.mediaSession?.setPositionState &&
    Number.isFinite(audio.duration) &&
    audio.duration > 0
  ) {
    try {
      navigator.mediaSession.setPositionState({
        duration: audio.duration,
        position: Math.min(audio.duration, Math.max(0, audio.currentTime)),
        playbackRate: audio.playbackRate || 1,
      });
    } catch {}
  }
}
function playSelected() {
  const song = currentSelection();
  if (song) playSong(song, state.list);
  else toast("曲库暂无可播放的音乐");
}
function toggleFavorite(song = currentSelection()) {
  if (!song) return;
  const found = state.favorites.indexOf(song.key);
  if (found < 0) {
    state.favorites.push(song.key);
    state.savedSongs.push({
      ...song,
      url: song.source === "local" ? song.url : undefined,
    });
  } else {
    state.favorites.splice(found, 1);
    state.savedSongs = state.savedSongs.filter((s) => s.key !== song.key);
  }
  save("favorites", state.favorites);
  save("savedSongs", state.savedSongs);
  renderDetail();
  toast(found < 0 ? "已加入当前浏览器的喜欢" : "已取消喜欢");
}
function showFavorites() {
  const all = [...state.local, ...state.cloud, ...state.savedSongs];
  const list = all.filter(
    (s, i) =>
      state.favorites.includes(s.key) &&
      all.findIndex((t) => t.key === s.key) === i,
  );
  useCollection(list, "local", "我的喜欢");
  if (!list.length) toast("暂无喜欢的曲目，可在歌曲详情中添加。");
}
function back() {
  if (state.dialog) {
    closeDialog();
    return;
  }
  if (state.mode === "detail") {
    state.mode = "archive";
    scene.setDetail(false);
    $("detailPanel").hidden = true;
    renderArchive();
    return;
  }
  showLibrary();
}
const actions = {
  library: showLibrary,
  "import-music": () => library.openImport(),
  "lyrics-line": () => player.setLyricMode("line"),
  "lyrics-word": () => player.setLyricMode("word"),
  "lyrics-earlier": () => player.adjustLyricOffset(-0.2),
  "lyrics-later": () => player.adjustLyricOffset(0.2),
  "lyrics-reset": () => player.adjustLyricOffset(-player.lyricOffset),
  "lyrics-follow": () => { manualLyricsUntil = 0; lastLyric = -2; renderLyrics(); },
  "lyrics-translation": () => {
    state.showTranslation = !state.showTranslation;
    save("showTranslation", state.showTranslation);
    player.autoTranslationEnabled = state.showTranslation;
    lyricsSignature = "";
    if (state.dialog === "lyrics") renderLyrics();
    if (state.showTranslation) player.ensureTranslations();
  },
  "lyrics-split": () => {
    state.splitLyrics = !state.splitLyrics;
    save("splitLyrics", state.splitLyrics);
    player.setSplitLongLyrics(state.splitLyrics);
    lyricsSignature = "";
    if (state.dialog === "settings") showSettings();
    if (state.dialog === "lyrics") renderLyrics();
  },
  "translation-retry": () => player.ensureTranslations(true),
  discover: showDiscover,
  search: showSearch,
  settings: showSettings,
  queue: showQueue,
  lyrics: showLyrics,
  back,
  detail: () => openDetail(),
  "close-dialog": closeDialog,
  "now-detail": () => {
    if (mobilePlayer.media.matches) {
      mobilePlayer.open();
      return;
    }
    if (player.current) openDetail(player.current);
    else openDetail();
  },
  "select-prev": () => select(state.selected - 1, { scroll: true }),
  "select-next": () => select(state.selected + 1, { scroll: true }),
  select: (el) => {
    const index = +el.dataset.index;
    if (index === state.selected) openDetail();
    else select(index);
  },
  slot: (el) => select(+el.dataset.index),
  "source-prev": showLibrary,
  "source-next": () => {
    if (state.cloud.length) useCollection(state.cloud, "cloud", "网易云音乐");
    else showSearch();
  },
  "tab-mode": () => {
    state.tabMode = !state.tabMode;
    save("tabMode", state.tabMode);
    renderArchive();
    centerTrackReel();
  },
  "play-selected": playSelected,
  "play-all": () => {
    if (state.list.length) {
      state.selected = 0;
      select(0);
      playSong(state.list[0], state.list);
    } else toast("曲库暂无可播放的音乐");
  },
  toggle: () => {
    const fresh = !player.current;
    const result = player.toggle(
      currentSelection(),
      player.current ? undefined : state.list,
    );
    if (fresh && currentSelection()) mobilePlayer.open();
    return result;
  },
  next: () => player.next(),
  "play-alternative": () => player.playAlternative(),
  previous: () => player.previous(),
  mode: () => {
    player.mode = { all: "one", one: "shuffle", shuffle: "all" }[player.mode];
    save("mode", player.mode);
    renderPlayer();
  },
  mute: () => {
    player.audio.muted = !player.audio.muted;
    renderPlayer();
  },
  enqueue: () => {
    player.enqueue(currentSelection());
    toast("已加入播放队列");
  },
  "play-next": () => {
    player.enqueue(currentSelection(), true);
    toast("已设为下一首播放");
  },
  "queue-play": (el) =>
    playSong(player.queue[+el.dataset.index], undefined, +el.dataset.index),
  "queue-remove": (el) => player.remove(+el.dataset.index),
  "clear-queue": () => player.clearPending(),
  "result-play": (el) =>
    playSong(state.results[+el.dataset.index], state.results),
  "detail-play": (el) => playSong(state.list[+el.dataset.index], state.list),
  "result-detail": (el) => {
    const list = state.dialog === "search" ? state.results : state.list;
    const index = +el.dataset.index;
    useCollection(
      list,
      list[index]?.source || "local",
      state.dialog === "search" ? "检索结果" : state.collection,
    );
    select(index);
    openDetail();
  },
  "search-cloud": () => {
    state.searchToken++;
    state.searching = false;
    state.searchSource = "cloud";
    state.results = [];
    state.searchError = "";
    renderSearch();
  },
  "search-local": () => {
    state.searchToken++;
    state.searching = false;
    state.searchSource = "local";
    state.results = [];
    state.searchError = "";
    renderSearch();
  },
  "theme-light": () => {
    setTheme("light");
    showSettings();
  },
  "theme-dark": () => {
    setTheme("dark");
    showSettings();
  },
  ...Object.fromEntries([85,90,100,110,120].map(value=>["font-"+value,()=>{
    state.fontScale = value / 100;
    save("fontScale", state.fontScale);
    applyFontScale();
    showSettings();
  }])),
  "flac-on": () => {
    player.preferOriginalFlac = true;
    save("preferOriginalFlac", true);
    showSettings();
  },
  "flac-off": () => {
    player.preferOriginalFlac = false;
    save("preferOriginalFlac", false);
    showSettings();
  },
  ...Object.fromEntries(["auto","balanced","high","low"].map(q=>["quality-"+q,()=>{scene.setQuality(q);showSettings();}])),
  "follow-on": () => {
    state.autoFollowPlayback = true;
    save("autoFollowPlayback", true);
    followPlayingSong();
    showSettings();
  },
  "follow-off": () => {
    state.autoFollowPlayback = false;
    save("autoFollowPlayback", false);
    showSettings();
  },
  "motion-on": () => {
    state.reduced = false;
    save("reduced", false);
    scene.setReduced(false);
    showSettings();
  },
  "motion-off": () => {
    state.reduced = true;
    save("reduced", true);
    scene.setReduced(true);
    showSettings();
  },
  "view-card": () => {
    state.tabMode = false;
    save("tabMode", false);
    showSettings();
  },
  "view-list": () => {
    state.tabMode = true;
    save("tabMode", true);
    showSettings();
  },
  "refresh-local": async () => {
    try {
      await loadLocal(true);
      toast("曲库已刷新，共 " + state.local.length + " 首");
      if (state.source === "local" && state.collection === "本地音乐") {
        state.list = state.local;
        select(Math.min(state.selected, state.list.length - 1));
      }
    } catch (error) {
      toast("刷新失败：" + error.message);
    }
  },
  favorite: () => toggleFavorite(),
  favorites: showFavorites,
  playlist: (el) => openPlaylist(+el.dataset.index),
  "replay-boot": () => {
    closeDialog();
    bootSequence?.replay();
  },
  "detail-info": () => {
    state.detailTab = "info";
    renderDetail();
  },
  "detail-album": () => {
    state.detailTab = "album";
    renderDetail();
  },
  "detail-same": () => {
    state.detailTab = "same";
    renderDetail();
  },
  "lyric-seek": (el) => {
    manualLyricsUntil = 0;
    const line = player.lyrics[+el.dataset.index];
    if (line && Number.isFinite(player.audio.duration))
      player.audio.currentTime = Math.max(0, line.time + player.lyricOffset);
  },
  artwork: () => {
    const song = currentSelection();
    if (!song?.cover) {
      toast("这首曲目暂无封面");
      return;
    }
    showDialog("artwork");
    $("dialogPanel").innerHTML =
      panelHeader("封面 / " + escape(song.title)) +
      `<img src="${escape(song.cover)}" alt="${escape(song.title)}的封面" style="width:100%;max-height:560px;object-fit:contain">`;
  },
};
document.addEventListener("click", (event) => {
  const el = event.target.closest("[data-action]");
  if (!el || el.disabled) return;
  const action = actions[el.dataset.action];
  if (action)
    Promise.resolve(action(el)).catch((error) => toast(error.message));
});
document.addEventListener("submit", (event) => {
  if (event.target.id === "searchForm") {
    event.preventDefault();
    search();
  }
});
document.addEventListener("input", (event) => {
  if (event.target.id === "searchInput") state.query = event.target.value;
  if (event.target.id === "seek") {
    event.target.style.setProperty(
      "--progress",
      Number(event.target.value) / 10 + "%",
    );
  }
  if (event.target.id === "volume") {
    player.audio.volume = Number(event.target.value) / 100;
    player.audio.muted = false;
    $("volumeControl").style.setProperty("--volume-percent", event.target.value + "%");
    save("volume", player.audio.volume);
  }
});
for (const type of ["wheel", "touchstart", "pointerdown"])
  document.addEventListener(
    type,
    (event) => {
      if (event.target.closest("#lyricsContent"))
        cancelLyricScroll();
      if (event.target.closest("#lyricsContent"))
        manualLyricsUntil = Date.now() + 6000;
    },
    { passive: true },
  );
$("seek").addEventListener("change", (e) =>
  player.seek(Number(e.target.value) / 1000),
);
document.addEventListener("keydown", (event) => {
  if (bootSequence?.active) return;
  if (mobilePlayer.opened) return;
  if (event.target.closest("input,textarea,select,[contenteditable=true]")) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.target.blur();
      back();
    }
    return;
  }
  if (event.key === "Escape") {
    event.preventDefault();
    back();
    return;
  }
  if (event.key === " " && event.target.tagName !== "BUTTON") {
    event.preventDefault();
    actions.toggle();
    return;
  }
  if (event.key === "/") {
    event.preventDefault();
    showSearch();
    return;
  }
  if (event.key.toLowerCase() === "l") {
    showLyrics();
    return;
  }
  if (state.dialog) return;
  const keyActions = {
    ArrowUp: "select-prev",
    ArrowDown: "select-next",
    ArrowLeft: "source-prev",
    ArrowRight: "source-next",
    Enter: "detail",
    t: "tab-mode",
  };
  if (
    keyActions[event.key] &&
    (event.key !== "Enter" || event.target.tagName !== "BUTTON")
  ) {
    event.preventDefault();
    actions[keyActions[event.key]]();
  }
});
let lastPlaybackKey = "";
player.addEventListener("change", (event) => {
  if (event.detail.error) toast(event.detail.error);
  const key = player.current?.key || "";
  if (key && key !== lastPlaybackKey && state.autoFollowPlayback)
    followPlayingSong();
  lastPlaybackKey = key;
  renderPlayer();
});
if ("mediaSession" in navigator)
  for (const [name, callback] of Object.entries({
    play: () => {
      if (player.audio.paused)
        player.toggle(
          player.current || currentSelection(),
          player.current ? undefined : state.list,
        );
    },
    pause: () => player.audio.pause(),
    previoustrack: () => player.previous(),
    nexttrack: () => player.next(),
    seekto: (e) => {
      if (Number.isFinite(e.seekTime)) player.audio.currentTime = e.seekTime;
    },
  })) {
    try {
      navigator.mediaSession.setActionHandler(name, callback);
    } catch {}
  }
addEventListener("resize", size);
// A desktop lyric view must hand off when a window becomes a phone layout.
mobilePlayer.media.addEventListener("change", ({matches}) => {
  if (matches && state.dialog === "lyrics") {
    closeDialog();
    mobilePlayer.open("lyrics");
  }
});
window.visualViewport?.addEventListener("resize", () => {
  size();
  scene.resize();
});
window.visualViewport?.addEventListener("scroll", size);
document.fonts.ready.then(() => {
  scene.paintLabel();
  scene.invalidate();
});
setTheme(state.theme === "dark" ? "dark" : "light");
size();
renderArchive();
renderPlayer();
showAllLibrary();
const bootParams = new URLSearchParams(location.search);
if (innerWidth > 900 && !state.reduced && !bootParams.has("bootOff")) {
  bootSequence = new BootSequence($("bootLayer"), {
    onDone: () => scene.invalidate(1200),
  });
  const freezeAt = Number(bootParams.get("bootAt"));
  if (Number.isFinite(freezeAt) && freezeAt > 0) bootSequence.update(freezeAt);
  else bootSequence.play();
} else {
  $("bootLayer").hidden = true;
}
// Small read-only diagnostics used by browser checks, without exposing state mutations.
Object.defineProperty(window, "rhineStatus", {
  get: () => ({
    source: state.source,
    tracks: state.list.length,
    selected: state.selected,
    view: state.mode,
    dialog: state.dialog,
    theme: state.theme,
    fontScale: state.fontScale ?? (innerWidth <= 900 ? 1 : 0.9),
    model: $("scene").dataset.model || "",
    renderer: $("scene").dataset.renderer || "loading",
    queue: player.queue.length,
    current: player.current?.title,
    playing: !player.audio.paused,
    time: player.audio.currentTime,
    duration: player.audio.duration,
    lyrics: player.lyrics.length,
    volume: player.audio.volume,
    mobilePlayer: mobilePlayer.opened,
    mobileTab: mobilePlayer.tab,
    sceneSuspended: Boolean(scene.obscured),
    sceneFrames: scene.renderCount || 0,
    quality: scene.quality,
    matrixUploads: scene.matrixUploads,
    colorUploads: scene.colorUploads,
    buffering: player.buffering,
  }),
});
